import React, { useState } from "react";
import { getAuthCookieAttributes, getCookie } from "shared/auth/utils/cookieUtils";
import { sendVerificationEmail } from "shared/api/client/orvalHelpers";
import { apiClient } from "shared/api/client/orval";

type EmailVerificationBannerProps = {
  className?: string;
  /** Called after the account was verified via the self-serve button. */
  onVerified?: () => void;
};

function errorMessage(error: unknown, fallback: string): string {
  const data = (error as any)?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data && typeof data === "object") {
    const m = data.error ?? data.message ?? data.title;
    if (typeof m === "string" && m) return m;
  }
  return fallback;
}

const EmailVerificationBanner: React.FC<EmailVerificationBannerProps> = ({
  className = "",
  onVerified,
}) => {
  const token = getCookie("userToken");
  const [message, setMessage] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleResend = async () => {
    if (!token) {
      setMessage("User not authenticated.");
      return;
    }
    try {
      await sendVerificationEmail();
      setMessage("New verification email sent!");
      setEmailSent(true);
    } catch (error) {
      setMessage("Error sending request.");
    }
  };

  const handleVerifySelf = async () => {
    if (!token) {
      setMessage("User not authenticated.");
      return;
    }
    setVerifying(true);
    setMessage("");
    try {
      await apiClient.post("/api/User/verify-self");
      document.cookie = `userIsVerified=true; ${getAuthCookieAttributes()}`;
      setMessage("Your email is now verified.");
      if (onVerified) {
        onVerified();
      } else {
        window.location.reload();
      }
    } catch (error) {
      setMessage(errorMessage(error, "Could not verify your account."));
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={className}>
      <div className="alert-message">
        <img
          src="/images/icons/triangle-warning.svg"
          alt="warning svg"
          className="alert-warning-library"
        />
        <p>Verify your email address. Check your inbox!</p>
      </div>
      {emailSent ? (
        <div className="verification-sent">
          <img
            src="/images/signup/check.png"
            alt="Success"
            className="checkmark-resend-verification"
          />
          <p className="resend-email-banner">{message}</p>
        </div>
      ) : (
        <>
          <button className="resend-email-banner" onClick={handleResend}>
            Resend verification email
          </button>
          {message && <div className="banner-message">{message}</div>}
        </>
      )}
      <button
        type="button"
        className="resend-email-banner"
        onClick={handleVerifySelf}
        disabled={verifying}
      >
        {verifying ? "Verifying…" : "Verify Email Now"}
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
